import { FetchNextPageOptions, InfiniteQueryObserverResult } from "@tanstack/react-query"
import { useEffect, useMemo, useRef, useState } from "react"
import { FlexItem } from "../../../../styled/flexbox.styled"
import useTradingDataQuery from "../Portfolio/hooks/useTradingDataQuery"
import Orders from "../Portfolio/Orders"

import { useQueryProfile } from "../../../hooks"
import { IAccountBookPairs } from "../types"
import useInfiniteWaves from "./hooks/useInfiniteWaves"
import {
  FullHeightOrdersContainer,
  NavDot,
  OrdersTableSection,
  QuickNav,
  TradingPageScroller,
  TradingWavePageContainer,
  WavesSection,
} from "./Trading.styled"
import { getDefaultOrderInputs } from "./types/form"
import { Wave } from "./types/wave"
import WaveCreationForm from "./WaveCreationForm"
import WaveDetail from "./WaveDetail"
import WaveList from "./WaveList"

interface ITradingProps {
  enableTradingQuery: boolean
  selectedFilters: IAccountBookPairs
}

const TradingBook = ({ enableTradingQuery, selectedFilters }: ITradingProps) => {
  /**
   * State to store the selected Wave
   */
  const [selectedWaveId, setSelectedWaveId] = useState<number | undefined>(undefined)
  const [liveOrders, setLiveOrders] = useState(true)

  /**
   * State to store the locate form values to be passed to the manual short locate modal
   */
  const [locateInstrumentQuery, setLocateInstrumentQuery] = useState<string | undefined>(undefined)
  const [locateQuantity, setLocateQuantity] = useState<number | undefined>(undefined)
  const [shortLocateModalOpen, setShortLocateModalOpen] = useState(false)
  const [activeSection, setActiveSection] = useState(0)
  const scrollerRef = useRef<HTMLDivElement>(null)

  /**
   * Get user profile to check if we enable Broker selection
   */
  const { data: profile } = useQueryProfile()

  /**
   * Check if user is in direct-orders group, if yes orders are sent to Flex
   * directly otherwise they are sent to S3 to go through the trading scanner.
   */
  const directOrdersEnabled = profile?.groups.includes("direct-orders") ?? false

  // Create default order for wave creation
  const defaultOrder = useMemo(
    () => getDefaultOrderInputs(directOrdersEnabled),
    [directOrdersEnabled],
  )

  /**
   * Get the live orders or all orders
   */
  const { tradingData, isLoading: isLoadingTradingData } = useTradingDataQuery({
    accountBookPairs: selectedFilters,
    liveOrders: liveOrders,
    enabled: enableTradingQuery,
  })

  /**
   * Get the filled orders
   */
  const { tradingData: tradingDataFilled, isLoading: isLoadingTradingDataFilled } =
    useTradingDataQuery({
      accountBookPairs: selectedFilters,
      liveOrders: false,
      enabled: enableTradingQuery,
    })
  const filledOrCanceledOrders = tradingDataFilled?.orders
    ? {
        ...tradingDataFilled.orders,
        rows: tradingDataFilled.orders.rows?.filter(
          (row: any) => row["status"] === "Filled" || row["status"] === "Canceled",
        ),
      }
    : undefined

  /**
   * Infinite hook to GET Trade Waves
   */
  const { waves, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage, status } =
    useInfiniteWaves()

  const selectedWave = useMemo(
    () => waves?.find(wave => wave.id === selectedWaveId),
    [waves, selectedWaveId],
  )

  /**
   * If we have some waves and that no waves are selected,
   * we set the first one as selected by default
   */
  useEffect(() => {
    if (!selectedWaveId && !!waves?.length) {
      setSelectedWaveId(waves[0].id)
    }
  }, [waves, selectedWaveId])

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.target as HTMLDivElement
    const scrollTop = target.scrollTop
    const scrollHeight = target.scrollHeight

    // Calculate which section is currently visible
    const sectionHeight = scrollHeight / 3
    const currentSection = Math.floor(scrollTop / sectionHeight)
    setActiveSection(currentSection)
  }

  const handleRightClickShortLocate = (rowData: any) => {
    setLocateInstrumentQuery(rowData["bb_symbol"])
    setLocateQuantity(rowData["quantity"])
    setShortLocateModalOpen(true)
  }

  const scrollToSection = (sectionIndex: number) => {
    if (scrollerRef.current) {
      const sectionHeight = scrollerRef.current.scrollHeight / 3
      scrollerRef.current.scrollTo({
        top: sectionIndex * sectionHeight,
        behavior: "smooth",
      })
    }
  }

  return (
    <TradingPageScroller ref={scrollerRef} onScroll={handleScroll}>
      <TradingWavePageContainer flexDirection="column">
        <FullHeightOrdersContainer>
          <WaveCreationForm
            setSelectedWaveId={setSelectedWaveId}
            directOrdersEnabled={directOrdersEnabled}
            defaultOrder={defaultOrder}
            shortLocateModalOpen={shortLocateModalOpen}
            setShortLocateModalOpen={setShortLocateModalOpen}
            locateInstrumentQuery={locateInstrumentQuery}
            setLocateInstrumentQuery={setLocateInstrumentQuery}
            locateQuantity={locateQuantity}
            setLocateQuantity={setLocateQuantity}
            setLiveOrders={setLiveOrders}
            liveOrders={liveOrders}
            previousWave={waves?.[0]}
          />
          <FlexItem flexGrow={1}>
            <Orders
              orders={tradingData?.orders}
              isLoading={isLoadingTradingData}
              shortLocateAction={handleRightClickShortLocate}
            />
          </FlexItem>
        </FullHeightOrdersContainer>

        <OrdersTableSection>
          <Orders orders={filledOrCanceledOrders} isLoading={isLoadingTradingDataFilled} />
        </OrdersTableSection>

        <WavesSection>
          <WaveList
            waves={waves}
            selectedWave={selectedWave}
            setSelectedWaveId={setSelectedWaveId}
            fetchNextPage={
              fetchNextPage as (
                options?: FetchNextPageOptions | undefined,
              ) => Promise<InfiniteQueryObserverResult<Wave[], unknown>>
            }
            hasNextPage={!!hasNextPage}
            isLoading={isFetching && !waves?.length}
            isFetchingNextPage={isFetchingNextPage}
            status={status}
          />
          <WaveDetail selectedWave={selectedWave} status={status} />
        </WavesSection>
      </TradingWavePageContainer>
      <QuickNav>
        <NavDot active={activeSection === 0} onClick={() => scrollToSection(0)} />
        <NavDot active={activeSection === 1} onClick={() => scrollToSection(1)} />
        <NavDot active={activeSection === 2} onClick={() => scrollToSection(2)} />
      </QuickNav>
    </TradingPageScroller>
  )
}

export default TradingBook
